<template>
  <div class="help-section">
    <v-btn 
      variant="text" 
      color="grey" 
      size="small" 
      @click="showHelp = !showHelp"
      class="help-toggle-btn"
    >
      <v-icon>{{ showHelp ? 'mdi-chevron-up' : 'mdi-help-circle-outline' }}</v-icon>
      {{ showHelp ? '도움말 접기' : '계산방식 도움말' }}
    </v-btn>

    <v-expand-transition>
      <div v-show="showHelp" class="help-content">
        <div class="help-box">
          <h4>🎯 이게 뭐하는 건가요?</h4>
          <p>
            <strong>생산직비과세</strong>를 적용하면 기관이 내야 할 4대보험료와 퇴직금이 줄어듭니다.<br>
            이 표는 "적용 전"과 "적용 후"를 비교해서 <strong>얼마나 절감되는지</strong> 보여줍니다.
          </p>
        </div>

        <div class="help-box">
          <h4>📊 개선전 vs 개선후</h4>
          <table class="help-table">
            <tr>
              <th></th>
              <th>개선전</th>
              <th>개선후</th>
            </tr>
            <tr>
              <td>생산직비과세</td>
              <td class="red">❌ 미적용</td>
              <td class="green">✅ 적용</td>
            </tr>
            <tr>
              <td>비과세 항목</td>
              <td>당직+중식+차량+양육만</td>
              <td>당직+중식+차량+양육 <strong>+ 연장야간휴일수당</strong></td>
            </tr>
            <tr>
              <td>과세금액</td>
              <td class="red">더 많음</td>
              <td class="green">더 적음</td>
            </tr>
            <tr>
              <td>기관부담 보험료</td>
              <td class="red">더 많이 냄</td>
              <td class="green">더 적게 냄</td>
            </tr>
          </table>
        </div>

        <div class="help-box">
          <h4>🧮 계산 방법</h4>
          
          <div class="calc-item">
            <strong>1. 비과세 합계</strong>
            <div class="calc-formula">
              <span class="label">개선전:</span> 당직수당 + 중식대 + 차량비 + 양육비
            </div>
            <div class="calc-formula">
              <span class="label">개선후:</span> 당직수당 + 중식대 + 차량비 + 양육비 + <span class="highlight">생산직비과세(연장야간휴일수당)</span>
            </div>
          </div>

          <div class="calc-item">
            <strong>2. 기관부담 4대보험</strong>
            <div class="calc-formula">
              과세금액을 기준으로 계산 (과세금액 ↑ = 보험료 ↑)
            </div>
            <div class="calc-detail">
              • 건강보험 = 과세금액 × 건강보험요율<br>
              • 장기요양 = 건강보험 × 장기요양요율<br>
              • 고용보험 = 과세금액 × 고용보험요율<br>
              • 실업급여 = 과세금액 × 실업급여요율<br>
              • 국민연금 = <span class="note">개선후와 동일값 사용 (별도 신고 기준)</span><br>
              • 산재보험 = <span class="note">개선후와 동일값 사용 (사업장 기준)</span>
            </div>
          </div>

          <div class="calc-item">
            <strong>3. 퇴직금</strong>
            <div class="calc-formula">
              (총급여 - 중식대) ÷ 12
            </div>
            <div class="calc-detail">
              • 중식대는 퇴직금 산정 기준에서 제외됩니다.
            </div>
          </div>

          <div class="calc-item">
            <strong>4. 기관 절감액</strong>
            <div class="calc-formula highlight-box">
              절감액 = 기관부담 차액 + 퇴직금 차액
            </div>
            <div class="calc-detail">
              • 마이너스(-) 값 = 💰 절감 (좋은 것!)<br>
              • 플러스(+) 값 = 증가 (나쁜 것)
            </div>
          </div>
        </div>

        <div class="help-box info-box">
          <h4>📅 서비스 시작연월 & 제외 대상</h4>
          <ul>
            <li><strong>조회연월:</strong> 현재 비교 분석 중인 연월</li>
            <li><strong>서비스 시작연월:</strong> 우리 기관이 임금서비스를 시작한 연월 (수정 가능)</li>
            <li><strong>제외 대상:</strong> 서비스 시작연월 <span class="red">이전에 퇴사</span>한 직원은 비교분석에서 제외됩니다.</li>
          </ul>
          <div class="info-detail">
            <strong>예시)</strong> 서비스 시작 2026년 4월<br>
            • 3월 퇴사자 → <span class="red">제외</span> (서비스 시작 전 퇴사)<br>
            • 4월 퇴사자 → <span class="green">포함</span> (서비스 시작 이후 퇴사)<br>
            • 5월 퇴사자 → <span class="green">포함</span>
          </div>
          <div class="info-detail" style="margin-top: 8px;">
            <strong>📋 임금대장:</strong> 퇴사연월이 자동 감지되어 제외됩니다.<br>
            <strong>📤 임금엑셀업로드:</strong> 제외 열의 <span class="checkbox-icon">☑</span> 체크박스로 직접 선택하세요.
          </div>
        </div>

        <div class="help-box tip-box">
          <h4>💡 알아두세요</h4>
          <ul>
            <li><strong>적용 대상:</strong> 요양보호사, 조리사, 조리원 (비과세 적용 가능)</li>
            <li><strong>비과세 한도:</strong> 연장·야간·휴일 수당 중 월 240만원까지</li>
            <li><strong>차액이 마이너스면:</strong> 기관이 절감하는 금액입니다!</li>
          </ul>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';

const showHelp = ref(false);
</script>

<style scoped>
.help-section {
  margin-bottom: 16px;
}

.help-toggle-btn {
  font-size: 12px;
}

.help-content {
  background-color: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  margin-top: 8px;
}

.help-box {
  background-color: #fff;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 14px;
  margin-bottom: 12px;
}

.help-box:last-child {
  margin-bottom: 0;
}

.help-box h4 {
  margin: 0 0 10px 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.help-box p {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #555;
}

.help-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.help-table th,
.help-table td {
  border: 1px solid #ddd;
  padding: 8px 10px;
  text-align: center;
}

.help-table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.help-table td:first-child {
  text-align: left;
  font-weight: 500;
}

.help-table .red {
  color: #d32f2f;
}

.help-table .green {
  color: #2e7d32;
}

.calc-item {
  margin-bottom: 14px;
  padding-bottom: 14px;
  border-bottom: 1px dashed #eee;
}

.calc-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.calc-item strong {
  display: block;
  font-size: 13px;
  margin-bottom: 6px;
  color: #333;
}

.calc-formula {
  font-size: 12px;
  color: #555;
  padding: 4px 0;
  padding-left: 12px;
}

.calc-formula .label {
  display: inline-block;
  min-width: 55px;
  color: #888;
}

.calc-formula .highlight {
  background-color: #fff3e0;
  padding: 1px 4px;
  border-radius: 3px;
  color: #e65100;
}

.calc-detail {
  font-size: 11px;
  color: #777;
  padding-left: 12px;
  margin-top: 4px;
  line-height: 1.7;
}

.calc-detail .note {
  color: #1565c0;
  font-style: italic;
}

.highlight-box {
  background-color: #e3f2fd;
  padding: 8px 12px !important;
  border-radius: 4px;
  font-weight: 500;
  color: #1565c0;
}

.info-box {
  background-color: #e3f2fd;
  border-color: #90caf9;
}

.info-box ul {
  margin: 0;
  padding-left: 20px;
  font-size: 12px;
  line-height: 1.8;
  color: #555;
}

.info-box li {
  margin-bottom: 4px;
}

.info-box .red {
  color: #d32f2f;
  font-weight: 600;
}

.info-box .green {
  color: #2e7d32;
  font-weight: 600;
}

.info-box .checkbox-icon {
  background-color: #ffebee;
  padding: 1px 4px;
  border-radius: 3px;
  color: #c62828;
  font-weight: 600;
}

.info-detail {
  background-color: #fff;
  border-radius: 4px;
  padding: 8px 12px;
  margin-top: 10px;
  font-size: 12px;
  color: #666;
  border-left: 3px solid #1976d2;
}

.tip-box {
  background-color: #fffde7;
  border-color: #fff176;
}

.tip-box ul {
  margin: 0;
  padding-left: 20px;
  font-size: 12px;
  line-height: 1.8;
  color: #555;
}

.tip-box li {
  margin-bottom: 4px;
}

.tip-box li:last-child {
  margin-bottom: 0;
}
</style>
